import { Component, computed, input, InputSignal, Signal } from '@angular/core';

import { CmsMedia } from '@shared/models/cms.model';
import {
    AddStrapiDomainNamePipe,
    withStrapiOrigin,
} from '@shared/pipes/add-strapi-domain-name/add-strapi-domain-name.pipe';

/** Native `loading` values this component accepts. */
export type CmsImageLoading = 'lazy' | 'eager';

/** One `srcset` candidate: an absolute URL and the intrinsic width it represents. */
interface SrcsetCandidate {
    readonly url: string;
    readonly width: number;
}

/**
 * Renders a Strapi media field as a plain `<img>`, resolving the URL to the CMS origin and applying
 * the media's intrinsic `width`/`height` (which reserve layout space and avoid CLS).
 *
 * **Serves the right size, not the original.** Strapi generates `thumbnail`/`small`/`medium`/`large`
 * derivatives on upload; without them in a `srcset` the browser has no choice but the full-resolution
 * file — three 2000×2000 avatars rendered at 104px were 15 MB of a 16 MB page. Pass `sizes` wherever
 * the display width is known (a CSS-sized avatar, a fixed-width logo) so the browser can pick a
 * candidate; with no `sizes` it assumes `100vw`, which is right for full-bleed imagery and harmless
 * elsewhere since the original remains the largest candidate.
 *
 * **Lazy by default.** Most CMS imagery sits below the fold, and eager loading there competes with
 * the above-the-fold render. Pass `loading="eager"` for anything in the first viewport — the header
 * logo does.
 *
 * Renders nothing when no media URL is present.
 *
 * Size it from a parent by setting the `--cms-image-width` / `--cms-image-height` custom properties
 * on the host element (regular class selectors can't reach the encapsulated `<img>`).
 */
@Component({
    selector: 'cms-image',
    imports: [AddStrapiDomainNamePipe],
    templateUrl: './cms-image.html',
    styleUrl: './cms-image.scss',
})
export class CmsImage {
    /** The Strapi media object to render. */
    public readonly media: InputSignal<CmsMedia | null | undefined> = input<
        CmsMedia | null | undefined
    >();

    /** Alt text used when the media has no `alternativeText` (pass an already-translated string). */
    public readonly fallbackAlt: InputSignal<string> = input<string>('');

    /**
     * The `sizes` attribute — how wide the image renders, e.g. `104px` or `(max-width: 560px) 100vw,
     * 400px`. Omit only when the image really is viewport-width.
     */
    public readonly sizes: InputSignal<string | null> = input<string | null>(null);

    /** Native `loading`; `eager` for anything in the first viewport. */
    public readonly loading: InputSignal<CmsImageLoading> = input<CmsImageLoading>('lazy');

    /**
     * `srcset` built from Strapi's derivatives plus the original, ascending by width. `null` when
     * there is nothing to choose between — a vector upload (SVG has no `formats`) or a single
     * candidate — so no attribute is emitted at all.
     */
    protected readonly srcset: Signal<string | null> = computed((): string | null => {
        const item: CmsMedia | null | undefined = this.media();

        if (!item?.url) {
            return null;
        }

        const byWidth: Map<number, string> = new Map<number, string>();

        for (const format of Object.values(item.formats ?? {})) {
            if (format?.url && format.width > 0) {
                byWidth.set(format.width, withStrapiOrigin(format.url));
            }
        }

        // The original is a candidate too, so a high-density display can still reach full quality.
        if (item.width && item.width > 0) {
            byWidth.set(item.width, withStrapiOrigin(item.url));
        }

        if (byWidth.size < 2) {
            return null;
        }

        return Array.from(byWidth.entries())
            .map(([width, url]: [number, string]): SrcsetCandidate => ({ url, width }))
            .sort((a: SrcsetCandidate, b: SrcsetCandidate): number => a.width - b.width)
            .map((candidate: SrcsetCandidate): string => `${candidate.url} ${candidate.width}w`)
            .join(', ');
    });
}
